import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@components/Header';
import Footer from '@components/Footer';
import { useAuth } from '@hooks/useAuth';
import { useVIPStatus } from '@hooks/useVIPStatus';
import { useVIPPackages, useCreatePayment, usePaymentStatus } from '@hooks/usePayment';
import { QRCodeSVG } from 'qrcode.react';
import './VIP.css';

// Scroll reveal hook - lightweight IntersectionObserver
function useScrollReveal() {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, []);

    return [ref, isVisible];
}

function VIP() {
    const navigate = useNavigate();
    const { user, isLoggedIn } = useAuth();
    const { isVIP, vipInfo, isLoading: vipLoading } = useVIPStatus();
    const { packages, isLoading: packagesLoading } = useVIPPackages();
    const { createPayment, loading: paymentLoading } = useCreatePayment();

    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showQR, setShowQR] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    // Scroll reveal refs
    const [heroRef, heroVisible] = useScrollReveal();
    const [cardShowcaseRef, cardShowcaseVisible] = useScrollReveal();
    const [pricingRef, pricingVisible] = useScrollReveal();
    const [benefitsRef, benefitsVisible] = useScrollReveal();

    const { paymentData: statusData } = usePaymentStatus(
        paymentData?.transaction_id,
        showQR
    );

    useEffect(() => {
        if (statusData?.payment_status === 'completed' || statusData?.verifyPaid) {
            setShowQR(false);
            setShowSuccess(true);
            setTimeout(() => {
                window.location.reload();
            }, 3000);
        }
    }, [statusData]);

    const handleContactSupport = () => {
        window.open('https://t.me/DinoBytes', '_blank');
    };

    const isPermanentVIP = vipInfo?.vip_status === 'vip_permanent' || vipInfo?.vip_status === 'permanent';

    const handleSubscribe = async (pkg) => {
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }

        if (isPermanentVIP) {
            alert('Anda sudah memiliki VIP Permanent! Tidak perlu perpanjang lagi.');
            return;
        }

        setError('');
        setSelectedPackage(pkg);

        const result = await createPayment(pkg.package_code);

        if (result.success) {
            setPaymentData(result.data);
            setShowQR(true);
        } else {
            setError(result.message || 'Gagal membuat pembayaran');
        }
    };

    const handleCloseQR = () => {
        setShowQR(false);
        setPaymentData(null);
        setSelectedPackage(null);
    };

    const getDaysRemaining = () => {
        if (!vipInfo?.vip_expires_at) return 0;
        const now = new Date();
        const expires = new Date(vipInfo.vip_expires_at);
        const diff = expires - now;
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    const daysRemaining = getDaysRemaining();

    return (
        <div className="vip-page-new">
            <Header />

            <main className="vip-main-content">
                {/* Premium Hero Section */}
                <section 
                    ref={heroRef} 
                    className={`vip-hero ${heroVisible ? 'reveal' : ''}`}
                >
                    <div className="vip-hero-badge">EXCLUSIVE MEMBERSHIP</div>
                    <div className="vip-hero-crown">👑</div>
                    <h1 className="vip-hero-title">
                        <span className="title-line">Unlock</span>
                        <span className="title-gold">Premium Experience</span>
                    </h1>
                    <p className="vip-hero-subtitle">
                        Rasakan sensasi menonton tanpa batas dengan akses VIP eksklusif
                    </p>
                </section>

                {/* Premium Golden Card Showcase */}
                <section 
                    ref={cardShowcaseRef}
                    className={`vip-card-showcase ${cardShowcaseVisible ? 'reveal' : ''}`}
                >
                    <div className="showcase-container">
                        <div className="showcase-card">
                            <div className="card-glow"></div>
                            <div className="gold-card">
                                <div className="card-pattern"></div>
                                <div className="card-chip"></div>
                                <div className="card-logo">DinoShort</div>
                                <div className="card-type">VIP MEMBER</div>
                                <div className="card-icon">👑</div>
                                <div className="card-text">PREMIUM ACCESS</div>
                                <div className="card-shine"></div>
                            </div>
                        </div>
                        <div className="showcase-info">
                            <h2 className="showcase-title">
                                Kartu Emas <span>Eksklusif</span>
                            </h2>
                            <p className="showcase-desc">
                                Dapatkan akses tak terbatas ke seluruh konten premium. 
                                Nikmati pengalaman menonton berkelas tanpa iklan yang mengganggu.
                            </p>
                            <ul className="showcase-features">
                                <li><span className="feature-check">✓</span> Tanpa Iklan Selamanya</li>
                                <li><span className="feature-check">✓</span> Akses Semua Episode</li>
                                <li><span className="feature-check">✓</span> Kualitas Video HD</li>
                                <li><span className="feature-check">✓</span> Support Prioritas</li>
                            </ul>
                            <button 
                                className="showcase-cta"
                                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                            >
                                Dapatkan Sekarang
                                <span className="cta-arrow">→</span>
                            </button>
                        </div>
                    </div>
                </section>

                {/* Current VIP Status */}
                {isVIP && (
                    <section className="vip-status-banner">
                        <div className={`vip-status-card ${isPermanentVIP ? 'permanent' : 'active'}`}>
                            <div className="vip-status-icon">
                                {isPermanentVIP ? '♾️' : '⏳'}
                            </div>
                            <div className="vip-status-info">
                                <h3 className="vip-status-title">
                                    {isPermanentVIP ? 'VIP Permanent Aktif!' : 'Status VIP Aktif'}
                                </h3>
                                <p className="vip-status-text">
                                    {isPermanentVIP 
                                        ? 'Anda memiliki akses VIP selamanya! 🎉'
                                        : `Berlaku hingga: ${new Date(vipInfo.vip_expires_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                    }
                                </p>
                                {!isPermanentVIP && daysRemaining <= 7 && (
                                    <p className="vip-status-warning">
                                        ⚠️ Tersisa {daysRemaining} hari lagi
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* Error Message */}
                {error && (
                    <div className="vip-error-banner">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                {/* Pricing Cards */}
                <section 
                    id="pricing"
                    ref={pricingRef}
                    className={`vip-pricing-section ${pricingVisible ? 'reveal' : ''}`}
                >
                    <div className="section-header">
                        <h2 className="vip-section-title">
                            <span className="title-accent">Pilih</span> Paket Anda
                        </h2>
                        <p className="section-subtitle">Investasi terbaik untuk hiburan tanpa batas</p>
                    </div>
                    
                    {packagesLoading || vipLoading ? (
                        <div className="vip-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <p>Memuat paket VIP...</p>
                        </div>
                    ) : (
                        <div className="vip-pricing-grid">
                            {packages.map((pkg, index) => {
                                const isPopular = pkg.package_code === '1month';
                                const isBestValue = pkg.package_code === '1year' || pkg.package_code === 'permanent';
                                
                                return (
                                    <div
                                        key={pkg.id}
                                        className={`vip-pricing-card ${isPopular ? 'popular' : ''} ${isBestValue ? 'best-value' : ''}`}
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        {isPopular && <div className="vip-badge popular-badge">🔥 POPULER</div>}
                                        {isBestValue && <div className="vip-badge best-value-badge">⭐ BEST VALUE</div>}
                                        
                                        <div className="vip-pricing-header">
                                            <h3 className="vip-package-name">{pkg.package_name}</h3>
                                            {pkg.duration_days && (
                                                <p className="vip-package-duration">
                                                    {pkg.package_code === 'permanent' ? '♾️ Selamanya' : `${pkg.duration_days} Hari`}
                                                </p>
                                            )}
                                        </div>

                                        <div className="vip-pricing-price">
                                            <span className="vip-currency">Rp</span>
                                            <span className="vip-amount">
                                                {parseInt(pkg.price).toLocaleString('id-ID')}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handleSubscribe(pkg)}
                                            disabled={paymentLoading || isPermanentVIP}
                                            className={`vip-subscribe-btn ${isPermanentVIP ? 'disabled' : ''}`}
                                        >
                                            {paymentLoading ? (
                                                <><i className="fas fa-spinner fa-spin"></i> Memproses...</>
                                            ) : isPermanentVIP ? (
                                                <><i className="fas fa-check"></i> VIP Aktif</>
                                            ) : (
                                                <>Pilih Paket</>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Benefits Section */}
                <section 
                    ref={benefitsRef}
                    className={`vip-benefits-section ${benefitsVisible ? 'reveal' : ''}`}
                >
                    <div className="section-header">
                        <h2 className="vip-section-title">
                            Mengapa <span className="title-accent">VIP</span>?
                        </h2>
                    </div>
                    <div className="vip-benefits-grid">
                        {[
                            { icon: '🚫', title: 'Bebas Iklan', desc: 'Tonton tanpa gangguan iklan apapun' },
                            { icon: '🎬', title: 'Full Akses', desc: 'Semua drama & episode tersedia' },
                            { icon: '⚡', title: 'HD Quality', desc: 'Streaming kualitas terbaik' },
                            { icon: '📱', title: 'Multi Device', desc: 'Akses di semua perangkat' }
                        ].map((benefit, index) => (
                            <div 
                                key={index} 
                                className="vip-benefit-card"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="vip-benefit-icon">{benefit.icon}</div>
                                <h3 className="vip-benefit-title">{benefit.title}</h3>
                                <p className="vip-benefit-text">{benefit.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            {/* QR Code Modal */}
            {showQR && paymentData && (
                <div className="vip-modal-overlay" onClick={handleCloseQR}>
                    <div className="vip-modal vip-payment-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="vip-modal-close" onClick={handleCloseQR}>
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="vip-modal-content">
                            <div className="vip-modal-header">
                                <div className="vip-modal-icon">💳</div>
                                <h2 className="vip-modal-title">Scan QR untuk Bayar</h2>
                            </div>
                            
                            <div className="vip-payment-info">
                                <div className="vip-payment-info-item">
                                    <span className="vip-payment-label">Paket:</span>
                                    <span className="vip-payment-value">{selectedPackage?.package_name}</span>
                                </div>
                                <div className="vip-payment-info-item">
                                    <span className="vip-payment-label">Total:</span>
                                    <span className="vip-payment-amount-value">
                                        Rp {parseInt(selectedPackage?.price).toLocaleString('id-ID')}
                                    </span>
                                </div>
                            </div>

                            {paymentData.qris_payload && (
                                <div className="vip-qr-container">
                                    <QRCodeSVG
                                        value={paymentData.qris_payload}
                                        size={280}
                                        level="M"
                                        className="vip-qr-code"
                                    />
                                </div>
                            )}

                            {paymentData.mock && (
                                <div className="vip-warning-box">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Mode Testing - QRIS Mock</span>
                                </div>
                            )}

                            <div className="vip-waiting-payment">
                                <i className="fas fa-circle-notch fa-spin"></i>
                                <span>Menunggu pembayaran...</span>
                            </div>

                            <p className="vip-payment-note">
                                <i className="fas fa-info-circle"></i>
                                Status otomatis update setelah pembayaran
                            </p>

                            <div className="vip-payment-help">
                                <button className="vip-help-btn" onClick={handleContactSupport}>
                                    <i className="fab fa-telegram-plane"></i>
                                    Butuh Bantuan?
                                </button>
                            </div>

                            <p className="vip-transaction-id">ID: {paymentData.transaction_id}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccess && (
                <div className="vip-modal-overlay">
                    <div className="vip-modal vip-success-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vip-modal-content">
                            <div className="vip-success-icon">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <h2 className="vip-success-title">Pembayaran Berhasil!</h2>
                            <p className="vip-success-message">
                                Selamat, benefit VIP Anda sudah aktif! 🎉
                            </p>
                            <div className="vip-success-loading">
                                <i className="fas fa-spinner fa-spin"></i>
                                <span>Memuat ulang halaman...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default VIP;
